import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { CampoCambiato, RigaAzione } from '@masamune/shared';
import { Icon } from '../../components/ui/Icon';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { MEmpty, MGroup, MMore, MRow } from '../../components/mobile/MobileKit';
import {
  ICONA_AVVISO, campoLungo, chiLHaChiesta, etichettaRiga, giornoCorto, giornoDiRoma, iconaRiga, oraDiRoma, perGiorno, quandoAzione,
  quandoDiRoma, statoBreve, testoAvviso, testoCampo, testoCampoCorto, testoStato, useAnnullaAzione, useRegistroAzioni,
  type AzioneNelRegistro,
} from './registroApi';
import { dopoAnnullo } from './carteApi';
import { useAssistenteStore } from '../../stores/assistente.store';

/**
 * «Cosa ha fatto MUNE» (28/10/2026, os-planning/ai/fase-1-progetto.md, decisione 15):
 * il registro delle azioni dell'Assistente, in Impostazioni › Servizi collegati.
 *
 * Per giorno (Oggi, Ieri, poi la data, in ora di Roma), ogni azione con l'ora, la frase
 * scritta dal codice, chi l'ha chiesta, le righe toccate con prima e dopo (già in parole
 * dall'API), gli avvisi partiti e lo stato. «Annulla» dove si può ancora: chiede conferma
 * e poi dice com'è andata. Le prime 10, poi «Mostra altre». Si vede e si annulla anche ad
 * Assistente spento: l'Annulla non chiama il modello.
 *
 * Computer: AzioniAssistente, dentro la scheda dell'Assistente (AssistenteCard).
 * Telefono: AzioniAssistenteMobile, a righe (SettingsMobile › Servizi collegati); ogni
 * azione apre il dettaglio in un pannello dal basso, con «Annulla» in fondo.
 */

const PASSO = 10;

/** Le azioni caricate e quante se ne vedono: 10 alla volta, dalle pagine da 20 dell'API. */
function useElenco() {
  const q = useRegistroAzioni();
  const [quante, setQuante] = useState(PASSO);
  const tutte: AzioneNelRegistro[] = q.data?.pages.flatMap((p) => p.items) ?? [];
  const mostraAltre = () => {
    const nuove = quante + PASSO;
    setQuante(nuove);
    if (tutte.length < nuove && q.hasNextPage && !q.isFetchingNextPage) void q.fetchNextPage();
  };
  return {
    tutte,
    visibili: tutte.slice(0, quante),
    altre: tutte.length > quante || !!q.hasNextPage,
    mostraAltre,
    caricando: q.isLoading,
    errore: q.isError,
    caricoAltre: q.isFetchingNextPage,
  };
}

// Dalla 2D (29/10/2026) MUNE non tocca solo le task: anche note, Diario, trattative e schede.
const VUOTO = "Per ora niente: qui compaiono le modifiche che MUNE fa nel gestionale, con l'Annulla.";
const SPIEGA_ANNULLA = "L'Annulla rimette solo quello che nessuno ha cambiato dopo.";

// ════════════════════════════════════════════════════════════════════
// Computer
// ════════════════════════════════════════════════════════════════════

export function AzioniAssistente() {
  const e = useElenco();
  const annulla = useAnnullaAzione();
  const giorni = perGiorno(e.visibili);

  return (
    <div>
      <div className="data-hd">
        <span className="tk-sez-t">Cosa ha fatto MUNE</span>
        {e.tutte.length > 0 && <span className="mute text-[12.5px]">{SPIEGA_ANNULLA}</span>}
      </div>

      {e.caricando ? (
        <div className="mute">Carico il registro…</div>
      ) : e.errore ? (
        <div className="mute">Il registro non si è caricato: riprova tra un momento.</div>
      ) : !e.tutte.length ? (
        <div className="mute">{VUOTO}</div>
      ) : (
        giorni.map((g) => (
          <section key={g.giorno} className="mt-4 first:mt-0">
            <h4 className="text-[12.5px] font-semibold text-ink">{g.etichetta}</h4>
            {g.azioni.map((a) => <Azione key={a.id} a={a} annulla={annulla} />)}
          </section>
        ))
      )}

      {e.altre && !e.caricando && (
        <button className="btn btn-sm btn-ghost mt-2" disabled={e.caricoAltre} onClick={e.mostraAltre}>
          <Icon name="chevronD" size={10} /> {e.caricoAltre ? 'Carico…' : 'Mostra altre'}
        </button>
      )}
    </div>
  );
}

function Azione({ a, annulla }: { a: AzioneNelRegistro; annulla: ReturnType<typeof useAnnullaAzione> }) {
  const quando = quandoAzione(a);
  const stato = testoStato(a);
  const questa = annulla.isPending && annulla.variables?.id === a.id;
  return (
    <div className="adm-line items-start">
      <div className="mono mute w-11 shrink-0" title={quandoDiRoma(quando)}>{oraDiRoma(quando)}</div>
      <div className="min-w-0 flex-1">
        <div className="text-ink">{a.frase}</div>
        <div className="mute text-[12.5px] mt-0.5">
          {chiLHaChiesta(a)}
          {stato && <> · <span className={a.stato === 'FALLITA' ? 'text-alert' : undefined}>{stato}</span></>}
        </div>
        {a.righe.length > 0 && (
          <ul className="mt-2 space-y-1.5">
            {a.righe.map((r) => <Riga key={`${r.tipo}-${r.id}`} r={r} />)}
          </ul>
        )}
        {a.avvisi.length > 0 && (
          <div className="mt-2">
            <div className="mute text-[12.5px]">Avvisi partiti</div>
            {a.avvisi.map((v, i) => (
              <div key={`${v.canale}-${i}`} className="flex items-center gap-1.5 text-[12.5px] text-ink">
                <Icon name={ICONA_AVVISO[v.canale]} size={11} className="mute shrink-0" /> {testoAvviso(v)}
              </div>
            ))}
          </div>
        )}
      </div>
      {a.annullabile && (
        // Riuscito, anche la chat lo sa subito: la riga «Fatto … · Annulla» o la riga della carta
        // dicono «Annullato» (29/10/2026, dalla revisione della 2D).
        <button className="btn btn-sm btn-ghost shrink-0" disabled={annulla.isPending} onClick={() => annulla.mutate(a, { onSuccess: (esito) => { if (esito) dopoAnnullo(a.id, esito); } })}>
          <Icon name="undo" size={10} /> {questa ? 'Annullo…' : 'Annulla'}
        </button>
      )}
    </div>
  );
}

/**
 * Una riga toccata (task, riga di scheda e, dalla 2D, nota del cliente, Diario, trattativa,
 * evento dello storico): cosa le è successo, il link, i campi prima e dopo.
 */
function Riga({ r }: { r: RigaAzione }) {
  return (
    <li className="text-[13px]">
      <span className="mute">{etichettaRiga(r)}:</span>{' '}
      {r.link ? <Link to={r.link} className="text-ink hover:underline">{r.titolo}</Link> : <span className="text-ink">{r.titolo}</span>}
      {r.campi.map((c, i) => <Campo key={`${c.campo}-${i}`} c={c} />)}
    </li>
  );
}

/** «Scadenza: gio 29/10, poi lun 2/11» in fila; una descrizione su più righe, accorciata. */
function Campo({ c }: { c: CampoCambiato }) {
  if (!campoLungo(c)) return <div className="mute text-[12.5px] pl-3">{testoCampo(c)}</div>;
  return (
    <div className="text-[12.5px] pl-3 mt-0.5">
      <div className="mute">{c.campo}</div>
      {c.prima != null && <div className="mute line-clamp-2 whitespace-pre-wrap">Prima: {c.prima}</div>}
      {/* Un testo nuovo (Diario, nota, descrizione; 2D, 29/10/2026) non ha un prima: «Dopo:» da solo non dice niente. */}
      <div className="text-ink line-clamp-3 whitespace-pre-wrap">{c.prima != null ? 'Dopo: ' : ''}{c.dopo ?? 'niente'}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Telefono
// ════════════════════════════════════════════════════════════════════

/**
 * `onLink` (29/10/2026, fetta 2B): dentro il pannello di MUNE, che al telefono copre tutto lo
 * schermo, un tocco su una task chiude il pannello e apre la pagina, come i riquadri della
 * chat; senza, la pagina cambierebbe sotto il pannello. In Impostazioni non c'è: si va e basta.
 */
export function AzioniAssistenteMobile({ onLink }: { onLink?: (href: string) => void } = {}) {
  const e = useElenco();
  const annulla = useAnnullaAzione();
  const navigate = useNavigate();
  const [apertaId, setApertaId] = useState<string | null>(null);
  const aperta = apertaId ? e.tutte.find((a) => a.id === apertaId) ?? null : null;
  const oggi = giornoDiRoma(new Date());
  const statoAperta = aperta ? testoStato(aperta) : null;

  // Al telefono la pagina del cliente è la scheda contatto, senza «Cosa sa MUNE»: una
  // riga ricordata apre la scheda nel pannello (28/10/2026). Le altre righe aprono la loro
  // pagina: la task, e dalla 2D la scheda del cliente (note), il Diario e la trattativa.
  const apriRiga = (r: RigaAzione) => {
    setApertaId(null);
    const cliente = r.tipo === 'memoria' ? /^\/clients\/([^/?#]+)/.exec(r.link ?? '')?.[1] : undefined;
    if (cliente) {
      const nome = r.campi.find((c) => c.campo === 'Scheda')?.dopo ?? 'Cliente';
      useAssistenteStore.getState().apriMemoria({ tipo: 'cliente', id: decodeURIComponent(cliente), nome });
    } else if (r.link) {
      if (onLink) onLink(r.link);
      else navigate(r.link);
    }
  };

  return (
    <>
      <MGroup title="Cosa ha fatto MUNE" note={e.tutte.length ? SPIEGA_ANNULLA : undefined}>
        {e.caricando ? (
          <MEmpty>Carico il registro…</MEmpty>
        ) : e.errore ? (
          <MEmpty>Il registro non si è caricato: riprova tra un momento.</MEmpty>
        ) : !e.visibili.length ? (
          <MEmpty>{VUOTO}</MEmpty>
        ) : e.visibili.map((a) => {
          const quando = quandoAzione(a);
          return (
            <MRow
              key={a.id}
              text={oraDiRoma(quando)}
              tone={a.stato === 'FALLITA' ? 'alert' : undefined}
              title={a.frase}
              sub={[giornoCorto(giornoDiRoma(quando), oggi), a.chi?.nome ?? 'di notte', statoBreve(a)].filter(Boolean).join(' · ')}
              onClick={() => setApertaId(a.id)}
            />
          );
        })}
        {e.altre && !e.caricando && <MMore label={e.caricoAltre ? 'Carico…' : 'Mostra altre'} onClick={e.mostraAltre} />}
      </MGroup>

      <MobileSheet
        open={!!aperta}
        onClose={() => setApertaId(null)}
        title={aperta?.frase}
        footer={aperta && aperta.annullabile ? (
          // flex-1: il fondo del pannello è una riga flex, e senza il tasto restava largo
          // quanto la sua scritta invece di prendere tutta la riga come gli altri (28/10/2026).
          <div className="mk-sheet-ft flex-1">
            <button
              type="button"
              className="btn btn-primary m-block"
              disabled={annulla.isPending}
              onClick={() => annulla.mutate(aperta, {
                onSuccess: (esito) => {
                  if (!esito) return;
                  dopoAnnullo(aperta.id, esito);
                  setApertaId(null);
                },
              })}
            >
              <Icon name="undo" size={14} /> {annulla.isPending ? 'Annullo…' : 'Annulla'}
            </button>
          </div>
        ) : undefined}
      >
        {aperta && (
          // grid-cols-1 (una colonna minmax(0, 1fr)): con la colonna automatica i titoli lunghi
          // su una riga allargavano il pannello oltre lo schermo e tagliavano i valori a destra.
          <div className="grid grid-cols-1 gap-4">
            <div className="mk-list">
              <MRow title="Quando" trail={quandoDiRoma(quandoAzione(aperta))} trailTone="mute" />
              <MRow title="Chi l'ha chiesta" trail={aperta.chi?.nome ?? 'nessuno, di notte'} trailTone="mute" />
              {statoAperta && <MRow title="Stato" sub={statoAperta} subWrap />}
            </div>
            {aperta.righe.length > 0 && (
              <MGroup title="Cosa ha toccato" count={aperta.righe.length}>
                {aperta.righe.map((r) => (
                  <MRow
                    key={`${r.tipo}-${r.id}`}
                    icon={iconaRiga(r)}
                    title={r.titolo}
                    sub={[etichettaRiga(r), ...r.campi.map(testoCampoCorto)].join(' · ')}
                    subWrap
                    onClick={r.link ? () => apriRiga(r) : undefined}
                  />
                ))}
              </MGroup>
            )}
            {aperta.avvisi.length > 0 && (
              <MGroup title="Avvisi partiti" count={aperta.avvisi.length} note="Le mail e i messaggi già partiti restano anche con l'Annulla.">
                {aperta.avvisi.map((v, i) => (
                  <MRow key={`${v.canale}-${i}`} icon={ICONA_AVVISO[v.canale]} title={testoAvviso(v)} sub={quandoDiRoma(v.il)} />
                ))}
              </MGroup>
            )}
          </div>
        )}
      </MobileSheet>
    </>
  );
}
